import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ShieldCheck, ArrowRight, FileText } from "lucide-react";
import { getSharedAnalysis } from "@/utils/share.functions";

const SITE = "https://legaldecoder.io";

type SharedAnalysis = {
  id: string;
  title: string | null;
  tool_slug: string | null;
  risk_level: "low" | "medium" | "high" | string | null;
  risk_score: number | null;
  result: any;
  created_at: string;
  shared_at: string | null;
};

type LoaderData = { found: true; analysis: SharedAnalysis } | { found: false };

function clamp(s: string, max: number) {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

function riskLabel(level: string | null | undefined) {
  if (level === "high") return "High Risk";
  if (level === "medium") return "Medium Risk";
  if (level === "low") return "Low Risk";
  return "Reviewed";
}

function buildMetaTitle(a: SharedAnalysis): string {
  const label = riskLabel(a.risk_level);
  const score = typeof a.risk_score === "number" ? ` (Score ${a.risk_score}/100)` : "";
  const subject = a.title?.trim() || (a.tool_slug ? a.tool_slug.replace(/-/g, " ") : "Contract");
  return clamp(`Contract Risk Briefing: ${label}${score} — ${subject} · legaldecoder`, 70);
}

function buildMetaDescription(a: SharedAnalysis): string {
  const summary: string = a.result?.verdict?.summary ?? "";
  const flagsCount: number = Array.isArray(a.result?.redFlags) ? a.result.redFlags.length : 0;
  const label = riskLabel(a.risk_level);
  const parts = [
    `${label}${typeof a.risk_score === "number" ? ` · ${a.risk_score}/100` : ""}.`,
    summary ? clamp(summary, 140) : "",
    flagsCount > 0 ? `${flagsCount} red flag${flagsCount === 1 ? "" : "s"} identified.` : "",
    "Inspected by legaldecoder.",
  ].filter(Boolean);
  return clamp(parts.join(" "), 200);
}

export const Route = createFileRoute("/shared/$token")({
  loader: async ({ params }): Promise<LoaderData> => {
    try {
      const res = await getSharedAnalysis({ data: { token: params.token } });
      if (!res.ok) return { found: false };
      return { found: true, analysis: res.analysis as SharedAnalysis };
    } catch {
      return { found: false };
    }
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/shared/${params.token}`;
    if (!loaderData || !loaderData.found) {
      return {
        meta: [
          { title: "Shared analysis not found — legaldecoder" },
          { name: "description", content: "This shared contract analysis is no longer available." },
          { name: "robots", content: "noindex, nofollow" },
          { property: "og:url", content: url },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const a = loaderData.analysis;
    const title = buildMetaTitle(a);
    const description = buildMetaDescription(a);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        // Private, time-sensitive shared content — keep out of indexes but
        // allow social crawlers to fetch OG tags for rich previews.
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "legaldecoder" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SharedAnalysisPage,
});

function SharedAnalysisPage() {
  const data = Route.useLoaderData();
  const router = useRouter();

  if (!data.found) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-4 py-16 text-center">
        <FileText className="mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">Shared analysis not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The link may have expired or been revoked by the owner.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          Analyze your own contract <ArrowRight className="h-4 w-4" />
        </Link>
      </main>
    );
  }

  const a = data.analysis;
  const result = a.result ?? {};
  const verdict = result.verdict ?? {};
  const redFlags: string[] = Array.isArray(result.redFlags) ? result.redFlags : [];
  const keyTerms: string[] = Array.isArray(result.keyTerms) ? result.keyTerms : [];
  const isHigh = a.risk_level === "high";

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-6 flex items-center justify-between">
        <Link to="/" className="text-sm font-semibold text-primary hover:underline">
          ← legaldecoder
        </Link>
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          Shared briefing
        </span>
      </header>

      <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {a.tool_slug ? a.tool_slug.replace(/-/g, " ") : "Contract analysis"}
        </div>
        <h1 className="text-2xl font-bold text-foreground">{a.title ?? "Contract analysis"}</h1>

        <div
          className={`mt-4 flex items-center gap-3 rounded-xl border px-4 py-3 ${
            isHigh
              ? "border-rose-200 bg-rose-50 text-rose-800"
              : a.risk_level === "medium"
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          {isHigh ? <AlertTriangle className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          <div className="leading-tight">
            <div className="text-xs font-bold uppercase tracking-wider opacity-80">
              {riskLabel(a.risk_level)}
            </div>
            <div className="text-base font-bold">
              {typeof a.risk_score === "number" ? `Score ${a.risk_score}/100` : "Reviewed"}
            </div>
          </div>
        </div>

        {verdict.summary && (
          <p className="mt-5 text-sm leading-relaxed text-foreground/90">{verdict.summary}</p>
        )}

        {redFlags.length > 0 && (
          <section className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Red flags
            </h2>
            <ul className="mt-2 space-y-1.5">
              {redFlags.map((f, i) => (
                <li key={i} className="text-sm text-rose-700">
                  • {f}
                </li>
              ))}
            </ul>
          </section>
        )}

        {keyTerms.length > 0 && (
          <section className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Key terms
            </h2>
            <ul className="mt-2 space-y-1.5">
              {keyTerms.map((t, i) => (
                <li key={i} className="text-sm text-foreground/90">
                  • {t}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-8 rounded-xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY. This briefing was generated by
          legaldecoder using a zero-data-retention pipeline.
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          Decode your own contract <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </main>
  );
  // Avoid unused-var lint
  void router;
}
